import { huskyThemedButtonProps } from "@husky-hrc/Utilities/Mixins/buttons";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridWidths } from "@insite/mobius/GridItem";
import Hidden from "@insite/mobius/Hidden";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, resolveColor } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    superHeading = "superHeading",
    heading = "heading",
    supportingText = "supportingText",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.superHeading]: string;
        [fields.heading]: string;
        [fields.supportingText]: string;
        [fields.links]: OwnLinkModel[];
    };
}

interface OwnLinkModel {
    fields: {
        label: string;
        destination: string;
        variant: "primary" | "secondary" | "tertiary";
        heading: string;
    };
}

const onClick = (url: string) => {
    window.open(url, "_blank");
};

export interface ContactFormWrapperStyles {
    submitButtonStyles?: ButtonPresentationProps;
    superHeadingStyles?: TypographyPresentationProps;
    headingStyles?: TypographyProps;
    supportingTextStyles: TypographyPresentationProps;
    contactUsButton?: ButtonPresentationProps;
    buttonHeadingStyles: TypographyPresentationProps;
    buttonComboWrapper: InjectableCss;
    buttonContainer: GridContainerProps;
    headerItemWrapper: InjectableCss;
    formWrapper: GridContainerProps;
    buttonColumn: InjectableCss;
    gridContainerStyles?: GridContainerProps;
}

const supportingTextCss = (lineHeight: string, fontSize: string) => {
    return css`
        p {
            color: ${getColor("text.main")};
            line-height: ${lineHeight};
            font-size: ${fontSize};
            letter-spacing: 0;
        }

        a > span {
            color: ${getColor("danger.main")} !important;
            text-decoration: underline;
            font-family: Lato, Arial, Helvetica, sans-serif;
        }
    `;
};

const contactFormWrapperStyles: ContactFormWrapperStyles = {
    buttonContainer: {
        css: css`
            margin-bottom: 60px;
            flex-grow: 0;
        `,
        offsetCss: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                        width: 100% !important;
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    formWrapper: {
        css: css`
            margin-top: 40px;
        `,
        offsetCss: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 15px 0;
                        }
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 15px 0;
                        }
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 15px 0;
                        }
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
    headerItemWrapper: {
        css: css`
            padding-bottom: 0;
            padding-top: 0;
        `,
    },
    buttonComboWrapper: {
        css: css`
            flex-direction: column;
            background-color: #f6f5fb;
            padding: 30px;
            gap: 20px;
        `,
    },
    buttonColumn: {
        css: css`
            flex-direction: column;
        `,
    },
    superHeadingStyles: {
        forwardAs: "h1",
        variant: "h5",
        css: css`
            text-transform: uppercase;
            color: ${getColor("common.backgroundContrast")};
            letter-spacing: 2px;
            outline: none;
        `,
    },
    headingStyles: {
        variant: "h1",
        as: "p",
        css: css`
            margin-bottom: 0.5em;
        `,
    },
    supportingTextStyles: {
        css: css`
            ${({ theme }) => {
                const textMain = resolveColor("text.main", theme);
                const danger = resolveColor("danger", theme);
                return breakpointMediaQueries(theme, [
                    supportingTextCss("26px", "16px"),
                    null,
                    null,
                    supportingTextCss("32px", "18px"),
                    null,
                ]);
            }}
        `,
    },
    buttonHeadingStyles: {
        variant: "h4",
        css: css`
            font-size: 22px;
            letter-spacing: 0;
            line-height: 27px;
        `,
    },
    submitButtonStyles: huskyThemedButtonProps(),
    contactUsButton: huskyThemedButtonProps(
        "primary",
        "slanted",
        css`
            margin-right: 30px;
            width: 100%;
            max-width: 227px;
        `,
    ),
    gridContainerStyles: {
        offsetCss: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 0;
                        }
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 0;
                        }
                    `,
                    css`
                        margin: 0;
                        width: 100% !important;
                        > div {
                            padding: 0;
                        }
                    `,
                    css``,
                    css``,
                ])}
        `,
    },
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const links = ownProps.fields.links?.map(link => link.fields);

    return {
        links,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const styles = contactFormWrapperStyles;

const ContactFormWrapper: React.FC<Props> = ({ id, fields, links }) => {
    const buttonLinks = links?.map((link, index) => {
        return (
            // eslint-disable-next-line react/no-array-index-key
            <GridContainer key={index} {...styles.buttonContainer}>
                <GridItem width={12 as GridWidths} {...styles.buttonComboWrapper}>
                    <Typography {...styles.buttonHeadingStyles}>{link.heading}</Typography>
                    <Button
                        {...styles.contactUsButton}
                        variant={link.variant}
                        onClick={() => onClick(link.destination)}
                    >
                        {link.label || link.destination}
                    </Button>
                </GridItem>
            </GridContainer>
        );
    });

    return (
        <>
            <GridContainer {...styles.gridContainerStyles}>
                <GridItem width={[12, 12, 12, 8, 8]}>
                    <GridContainer {...styles.gridContainerStyles}>
                        <GridItem width={12 as GridWidths} {...styles.headerItemWrapper}>
                            <Typography tabIndex={-1} data-id="PageTitle" {...styles.superHeadingStyles}>
                                {fields.superHeading}
                            </Typography>
                        </GridItem>
                        <GridItem width={12 as GridWidths} {...styles.headerItemWrapper}>
                            <Typography {...styles.headingStyles}>{fields.heading}</Typography>
                        </GridItem>
                        {fields.supportingText && (
                            <GridItem width={12 as GridWidths} {...styles.headerItemWrapper}>
                                <Typography {...styles.supportingTextStyles}>
                                    {parse(fields.supportingText, parserOptions)}
                                </Typography>
                            </GridItem>
                        )}
                        <GridItem width={12 as GridWidths}>
                            <Zone zoneName="HubspotForm" contentId={id} />
                        </GridItem>
                    </GridContainer>
                </GridItem>

                <Hidden below="lg">
                    <GridItem width={1 as GridWidths}></GridItem>
                </Hidden>

                <GridItem width={[12, 12, 12, 3, 3]} {...styles.buttonColumn}>
                    {buttonLinks}
                </GridItem>
            </GridContainer>
        </>
    );
};

const contactFormWrapper: WidgetModule = {
    component: connect(mapStateToProps)(ContactFormWrapper),
    definition: {
        displayName: "Contact Form Wrapper",
        group: "Common",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.superHeading,
                editorTemplate: "TextField",
                displayName: "Super Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                fieldType: "Translatable",
                name: fields.supportingText,
                editorTemplate: "RichTextField",
                displayName: "Supporting Text",
                defaultValue: "",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.label;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "heading",
                        displayName: "Link Heading",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "label",
                        displayName: "Link Label",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "destination",
                        displayName: "Link",
                        editorTemplate: "TextField",
                        defaultValue: "/",
                        isRequired: true,
                    },
                    {
                        name: "variant",
                        displayName: "Variant",
                        editorTemplate: "DropDownField",
                        options: [
                            { displayName: "Primary", value: "primary" },
                            { displayName: "Secondary", value: "secondary" },
                            { displayName: "Tertiary", value: "tertiary" },
                        ],
                        hideEmptyOption: true,
                        defaultValue: "primary",
                    },
                ],
            },
        ],
    },
};

export default contactFormWrapper;
